/* eslint-disable react-hooks/exhaustive-deps */
import {useQuery} from "@tanstack/react-query"
import {Call, Device} from "@twilio/voice-sdk"
import {handleError, toastInfo} from "helpers"
import {SocketEvent} from "helpers/socket"
import {useModel} from "hooks"
import {createContext, useCallback, useEffect, useRef, useState} from "react"
import {autoDialerService, coreSocket, studentCallLogService, zoomService} from "services"
import {AutoDialer} from "types/auto-dialer"
import {Settings} from "types/settings"
import {Zoom} from "types/zoom"
import {useMutation} from "@tanstack/react-query"
import {StudentCallLog} from "types/student-call-log"
import {DialerStudent} from "types/auto-dialer"

type ContextProps = {
    calling: boolean
    voiceProvider: Settings.DialerProvider
    startCall: (student?: DialerStudent, departmentId?: number) => void
    hangup: () => void
    activeStudent: DialerStudent | null
    activeDepartmentId: number | null
    queuedStudents: DialerStudent[]
    setQueuedStudents: (students: DialerStudent[]) => void
    callStatus: string
    isConnected: boolean
    isAuto: boolean
    setIsAuto: (isAuto: boolean) => void
    acceptCall: () => void
    rejectCall: () => void
    isIncomingCall: boolean
    isInTransitionCall: boolean
    transitionCountdown: number
    connectedTime: Date | null
    forceCloseCall: () => void
    onEndedCallback: (callback: () => void) => void
    toggleMute: () => void
    isMuted: boolean
    activeCall: Call | null
}

export const VoiceCallContext = createContext<ContextProps>({} as ContextProps)

type VoiceCallProviderProps = {
    children: React.ReactNode
}

let device: Device | null = null

// Helper function to parse URL-encoded query string
const parseQueryString = (queryString: string): Record<string, string> => {
    const params = new URLSearchParams(queryString)
    const result: Record<string, string> = {}

    for (const [key, value] of params.entries()) {
        result[key] = value
    }

    return result
}

export const VoiceCallProvider: React.FC<VoiceCallProviderProps> = ({children}) => {
    // information
    const [activeStudent, setActiveStudent] = useState<DialerStudent | undefined>(undefined)
    const [queuedStudents, setQueuedStudents] = useState<DialerStudent[]>([])
    const [activeDepartmentId, setActiveDepartmentId] = useState<number | undefined>()
    const [isAuto, setIsAuto] = useState(false)

    // status
    const [calling, setCalling] = useState(false)
    const [deviceStatus, setDeviceStatus] = useState<string>()
    const [callStatus, setCallStatus] = useState<string>()
    const [activeCall, setActiveCall] = useState<Call | null>(null)
    const [isIncomingCall, setIsIncomingCall] = useState(false)
    const lastStatusRef = useRef(callStatus)
    const onEndedCallRef = useRef<(() => void) | null>(null)
    const [isMuted, setIsMuted] = useState(false)

    // provider
    const [zoomAccount, setZoomAccount] = useState<Zoom.ZoomAccount | undefined>()

    const model = useModel()
    const voiceProvider = model.clientSetting.dialerProvider
    const slug = model.slug
    const staffProfileId = model.profileId
    const isConnected = callStatus === AutoDialer.DialerStatus.Connected
    const [isInTransitionCall, setIsInTransitionCall] = useState(false)
    const [transitionCountdown, setTransitionCountdown] = useState(10)
    const [connectedTime, setConnectedTime] = useState<Date | null>(null)

    useEffect(() => {
        const handleBeforeUnload = (e: BeforeUnloadEvent) => {
            if (calling) {
                e.preventDefault()
                return "You have an active call. Are you sure you want to leave?"
            }
        }

        window.addEventListener("beforeunload", handleBeforeUnload)
        return () => {
            window.removeEventListener("beforeunload", handleBeforeUnload)
        }
    }, [calling])

    useQuery({
        queryKey: ["twilio-token"],
        queryFn: () => autoDialerService.getTwilioToken(),
        enabled: voiceProvider === Settings.DialerProvider.Twilio,
        refetchOnMount: true,
        onSuccess: (data) => {
            device = new Device(data.token)

            device.on("error", (error) => {
                // Unhandled error
                console.log("[Twilio] Error", error)
            })
            device.on("registered", () => setDeviceStatus("connected"))
            device.on("tokenWillExpire", async () => {
                console.log("[Twilio] Token will expire soon, refreshing...")
                const {token} = await autoDialerService.getTwilioToken()
                device.updateToken(token)
                console.log("[Twilio] Token updated")
            })
            device.on("destroyed", () => {
                setDeviceStatus("disconnected")
                setCalling(false)
            })
            device.on("incoming", (call) => {
                const student = call.parameters.Params
                const studentParams = parseQueryString(student)
                const mappedStudent: DialerStudent = {
                    userId: parseInt(studentParams.userId),
                    profileId: parseInt(studentParams.profileId),
                    fullName: studentParams.fullName,
                    email: studentParams.email || "", // Default empty string if email not provided
                    phone: studentParams.phone,
                    campusName: studentParams.campusName
                }
                setActiveDepartmentId(parseInt(studentParams.departmentId))
                setActiveStudent(mappedStudent)
                setCallStatus(AutoDialer.DialerStatus.Initial)
                setCalling(true)
                setIsIncomingCall(true)

                call.on("cancel", resetCall)
                call.on("mute", (muted) => setIsMuted(muted))
                setActiveCall(call)
            })

            device.register()
        }
    })

    useQuery({
        queryKey: ["zoom-account"],
        queryFn: () => zoomService.getMyZoomAccount(),
        enabled: voiceProvider === Settings.DialerProvider.Zoom,
        refetchOnMount: true,
        onSuccess: (data) => {
            setZoomAccount(data)
        }
    })

    const resetCall = useCallback(() => {
        setCalling(false)
        setCallStatus(AutoDialer.DialerStatus.Initial)
        setIsIncomingCall(false)
        setActiveStudent(null)
        setActiveDepartmentId(null)
        setActiveCall(null)
        setIsMuted(false)
    }, [])

    const startCall = useCallback(
        (student: DialerStudent, departmentId?: number) => {
            setIsInTransitionCall(false)
            setTransitionCountdown(0)
            setConnectedTime(null)

            setActiveStudent(student)
            setActiveDepartmentId(departmentId)

            if (voiceProvider === Settings.DialerProvider.Twilio) {
                let fromPhoneNumber = model.smsPhoneNumbers.length > 0 ? model.smsPhoneNumbers[0] : null
                if (!fromPhoneNumber) {
                    const twilioPhoneNumber = model.smsAllPhoneNumbers.find(
                        (phone) => phone.departmentIdForDialer === departmentId
                    )
                    if (!twilioPhoneNumber) {
                        toastInfo("You haven't set up a phone number for this department")
                        return
                    }
                    fromPhoneNumber = twilioPhoneNumber
                }

                let params: any = {
                    from: fromPhoneNumber.phone,
                    to: student.phone,
                    staffProfileId: staffProfileId.toString(),
                    slug
                }
                if (student.profileId) {
                    params.studentProfileId = student.profileId.toString()
                }
                if (departmentId) {
                    params.departmentId = departmentId.toString()
                }
                if (student.dialerId) {
                    params.dialerId = student.dialerId.toString()
                }
                device.connect({params}).then((call) => {
                    call.on("mute", (muted) => setIsMuted(muted))
                    setActiveCall(call)
                })
                setCalling(true)
                setCallStatus(AutoDialer.DialerStatus.Initial)
            }
            if (voiceProvider === Settings.DialerProvider.Zoom) {
                if (!zoomAccount) {
                    toastInfo(
                        "You have not connected your Zoom account yet",
                        "Now we will redirect you to connect your account. After that, you can start the call."
                    )
                    setTimeout(() => {
                        grantZoomAccountMutation.mutate()
                    }, 1000)
                    return
                }
                logZoomCallMutation.mutate({
                    profileId: student.profileId,
                    phone: student.phone,
                    departmentId,
                    provider: Settings.DialerProvider.Zoom,
                    dialerId: student.dialerId
                } as any)
            }
        },
        [voiceProvider, zoomAccount]
    )

    const nextCall = useCallback(() => {
        const [nextStudent, ...rest] = queuedStudents
        setQueuedStudents(rest)
        if (nextStudent) {
            startCall(nextStudent, activeDepartmentId)
        } else {
            setIsAuto(false)
            resetCall()
        }
    }, [queuedStudents, activeDepartmentId, startCall])

    const onEndedCall = useCallback(() => {
        if (isAuto) {
            setIsInTransitionCall(true)
            setTimeout(nextCall, 10000)
        } else {
            resetCall()
        }
        onEndedCallRef.current?.()
    }, [isAuto, nextCall, resetCall, onEndedCallRef])

    useEffect(() => {
        if (isInTransitionCall) {
            setTransitionCountdown(10)
            const interval = setInterval(() => {
                setTransitionCountdown((prev) => {
                    if (prev > 1) return prev - 1
                    clearInterval(interval)
                    return 1
                })
            }, 1000)
            return () => clearInterval(interval)
        }
    }, [isInTransitionCall])

    useEffect(() => {
        if (callStatus === AutoDialer.DialerStatus.Ended && lastStatusRef.current !== callStatus) {
            onEndedCall()
        }
        lastStatusRef.current = callStatus
    }, [callStatus, onEndedCall])

    const handleDialerStatus = useCallback(
        (payload: AutoDialer.DialerSocketPayload) => {
            if (
                staffProfileId === payload.profileId &&
                (activeStudent?.profileId === payload.studentProfileId || activeStudent?.phone === payload.phone)
            ) {
                const {status: dialerStatus} = payload
                setCallStatus(dialerStatus)
                if (dialerStatus === AutoDialer.DialerStatus.Connected) {
                    setConnectedTime(new Date(payload.connectedAt))
                }
            }
        },
        [staffProfileId, activeStudent]
    )

    useEffect(() => {
        ;(async function listenDialerSocket() {
            coreSocket.on(SocketEvent.DialerUpdated, handleDialerStatus)
            return () => {
                coreSocket.off(SocketEvent.DialerUpdated, handleDialerStatus)
            }
        })()
    }, [handleDialerStatus])

    const hangup = useCallback(() => {
        device?.disconnectAll()
        setCallStatus(AutoDialer.DialerStatus.Initial)
        setActiveCall(null)
        if (isIncomingCall) resetCall()
    }, [device, isIncomingCall, resetCall])

    const acceptCall = useCallback(() => {
        if (activeCall) {
            activeCall.accept()
            setCallStatus(AutoDialer.DialerStatus.Connected)
            activeCall.on("disconnect", resetCall)
        }
    }, [activeCall])

    const rejectCall = useCallback(() => {
        activeCall?.reject()
        resetCall()
    }, [activeCall])

    const grantZoomAccountMutation = useMutation({
        mutationFn: () =>
            zoomService.grantAccount({
                backUrl: window.location.href
            }),
        onSuccess: (authorizedData) => {
            if (authorizedData && authorizedData.redirect === true) {
                window.location.href = authorizedData.redirectUri
            }
        },
        onError: (error) => {
            handleError(error)
        }
    })

    const logZoomCallMutation = useMutation({
        mutationFn: (params) => studentCallLogService.add(params),
        onSuccess: (data: StudentCallLog) => {
            setCallStatus(AutoDialer.DialerStatus.Initial)
            window.open(`zoomphonecall://${data.calleePhoneNumber}`, "_blank", "noopener,noreferrer")
            setCalling(true)
        },
        onError: (error) => {
            handleError(error)
        }
    })

    const forceCloseCall = useCallback(() => {
        device?.disconnectAll()
        resetCall()
    }, [device])

    const onEndedCallback = (f: () => void) => {
        onEndedCallRef.current = f
    }

    const toggleMute = useCallback(() => {
        if (voiceProvider === Settings.DialerProvider.Twilio && activeCall) {
            activeCall.mute(!isMuted)
        }
    }, [isMuted, activeCall, voiceProvider])

    return (
        <VoiceCallContext.Provider
            value={{
                calling,
                voiceProvider,
                startCall,
                hangup,
                activeStudent,
                activeDepartmentId,
                queuedStudents,
                setQueuedStudents,
                callStatus,
                isConnected,
                isAuto,
                setIsAuto,
                acceptCall,
                rejectCall,
                isIncomingCall,
                isInTransitionCall,
                transitionCountdown,
                connectedTime,
                forceCloseCall,
                onEndedCallback,
                toggleMute,
                isMuted,
                activeCall
            }}>
            {children}
        </VoiceCallContext.Provider>
    )
}
